// 品番原価の金額再計算(原価率・掛率・スロット別集計)をまとめる。
// 本文は HinbanMaster.js から移設したもの。

export function createGenkaRecalcService(deps) {
    const {
        DeepCopy,
        DicVal,
        GridApi,
        GridData,
        HINBAN_GENKA_PANEL_TYPE,
        HINBAN_GENKA_ROW_DEFS,
        SetGridData,
        buildHinbanGenkaDisplayRows,
        buildHinbanGenkaSaveRows,
        deriveGenkaPatternFromEntry,
        emptyHinbanGenkaRow,
        entrydataRef,
        fnc,
        genkaCurrency,
        genkaPattern,
        genkaPatternNo,
        hinbanGenkaRowKeyOrder,
        hinbanGenkaRowLabelByKey,
        isEmpty,
        rawHinbanGenkaRow,
        rerender,
        shouldForceGenkaCurrencyJpy
    } = deps;

    function hinbanGenkaToNumber(value) {
        const raw = String(value ?? '').replace(/,/g, '').trim();
        if (raw === '') return 0;
        const num = Number(raw);
        return Number.isFinite(num) ? num : 0;
    }
    function roundHinbanGenka(value) {
        const num = Number(value ?? 0);
        if (!Number.isFinite(num)) return 0;
        return Math.round((num + Number.EPSILON) * 10000) / 10000;
    }
    function calcHinbanGenkaRate(value) {
        return hinbanGenkaToNumber(value) / 100;
    }
    function hinbanGenkaMainSlotByPatternNo(no) {
        return [2, 6, 7].includes(Number(no)) ? 'yusho' : 'muryo';
    }
    function hinbanGenkaMainFSlotByPatternNo(no) {
        return Number(no) === 7 ? 'yusho_f' : 'muryo_f';
    }
    function hinbanGenkaExpenseSlotByPatternNo(no) {
        return [6, 7].includes(Number(no)) ? 'yusho' : 'muryo';
    }
    function hinbanGenkaPatternNoForCalc() {
        const stateNo = Number(genkaPatternNo);
        if (Number.isFinite(stateNo) && stateNo !== 0) return stateNo;
        const derived = Number(deriveGenkaPatternFromEntry(entrydataRef.current));
        if (Number.isFinite(derived) && derived !== 0) return derived;
        const matched = String(genkaPattern ?? '').match(/^\s*(\d+)/);
        return isEmpty(matched) ? 0 : Number(matched[1]);
    }
    function buildHinbanGenkaCalcRowMap(displayRows) {
        const map = {};
        HINBAN_GENKA_ROW_DEFS.forEach((def, index) => {
            map[def.key] = emptyHinbanGenkaRow(def, index);
        });
        (Array.isArray(displayRows) ? displayRows : []).forEach((row, index) => {
            const rowKey = String(DicVal(row, 'row_key', '') ?? '').trim();
            if (rowKey === '' || isEmpty(map[rowKey])) return;
            map[rowKey] = {
                ...map[rowKey],
                ...row,
                rowid: String(DicVal(row, 'rowid', index)),
                row_key: rowKey,
                item_name: String(DicVal(row, 'item_name', hinbanGenkaRowLabelByKey[rowKey] ?? ''))
            };
        });
        return map;
    }
    function applyHinbanGenkaDisplayRows(panelInfo, rawRows, displayRows) {
        SetGridData(panelInfo, rawRows);
        const api = GridApi(panelInfo);
        if (!isEmpty(api)) {
            if (typeof api.setGridOption === 'function') {
                api.setGridOption('rowData', displayRows);
            } else if (typeof api.setRowData === 'function') {
                api.setRowData(displayRows);
            }
            if (typeof api.refreshCells === 'function') api.refreshCells({ force: true });
        }
    }
    function recalcHinbanGenkaGridAndSummary(panelInfo) {
        if (String(DicVal(panelInfo, 'type', '')).trim() !== HINBAN_GENKA_PANEL_TYPE) return;
        const sourceRows = Array.isArray(GridData(panelInfo)) ? DeepCopy(GridData(panelInfo)) : [];
        const displayRows = buildHinbanGenkaDisplayRows(sourceRows);
        const rows = buildHinbanGenkaCalcRowMap(displayRows);
        const patternNo = hinbanGenkaPatternNoForCalc();
        if (!Number.isFinite(patternNo) || patternNo === 0) {
            applyHinbanGenkaDisplayRows(panelInfo, sourceRows, displayRows);
            rerender();
            return;
        }
        const rawRow = { ...(rawHinbanGenkaRow(sourceRows) || {}) };
        const main = hinbanGenkaMainSlotByPatternNo(patternNo);
        const mainF = hinbanGenkaMainFSlotByPatternNo(patternNo);
        const expenseSlot = hinbanGenkaExpenseSlotByPatternNo(patternNo);
        const rate = shouldForceGenkaCurrencyJpy(entrydataRef.current) ? 1 : hinbanGenkaToNumber(DicVal(rawRow, 'SSDGM_RATE', DicVal(entrydataRef.current, 'SSDGM_RATE', '0')));
        const n = (rowKey, slot) => hinbanGenkaToNumber(DicVal(rows[rowKey], slot, 0));
        const has = (rowKey, slot) => String(DicVal(rows[rowKey], slot, '') ?? '').trim() !== '';
        const w = (rowKey, slot, value) => { rows[rowKey][slot] = String(roundHinbanGenka(value)); };
        if ([5, 7, 9].includes(patternNo) && rate !== 0) {
            ['MAT_AMOUNT', 'SUB_AMOUNT', 'NAME_AMOUNT', 'DELIVERY_MAT', 'PROCESS', 'SECONDARY', 'PRODUCT'].forEach((rowKey) => {
                if (patternNo === 7 && has(rowKey, 'yusho_f')) rows[rowKey].yusho = String(roundHinbanGenka(n(rowKey, 'yusho_f') * rate));
                if (has(rowKey, 'kojo_f')) rows[rowKey].kojo = String(roundHinbanGenka(n(rowKey, 'kojo_f') * rate));
            });
            ['MAT_LOSS', 'SUB_LOSS', 'ETC_LOSS', 'MARGIN'].forEach((rowKey) => {
                if (patternNo === 7 && has(rowKey, 'yusho_f')) rows[rowKey].yusho = String(DicVal(rows[rowKey], 'yusho_f', ''));
                if (has(rowKey, 'kojo_f')) rows[rowKey].kojo = String(DicVal(rows[rowKey], 'kojo_f', ''));
            });
        }
        ['muryo', 'muryo_f', 'yusho', 'yusho_f', 'kojo', 'kojo_f'].forEach((slot) => {
            w('MAT_TOTAL', slot, n('MAT_AMOUNT', slot) * (1 + calcHinbanGenkaRate(n('MAT_LOSS', slot))));
            if (!has('MAT_AMOUNT', slot) && !has('MAT_LOSS', slot)) rows.MAT_TOTAL[slot] = '';
            const sub = n('SUB_AMOUNT', slot) * (1 + calcHinbanGenkaRate(n('SUB_LOSS', slot)));
            const name = n('NAME_AMOUNT', slot) * (1 + calcHinbanGenkaRate(n('ETC_LOSS', slot)));
            w('SUB_TOTAL', slot, sub + name + n('DELIVERY_MAT', slot));
            if (!has('SUB_AMOUNT', slot) && !has('SUB_LOSS', slot) && !has('NAME_AMOUNT', slot) && !has('ETC_LOSS', slot) && !has('DELIVERY_MAT', slot)) rows.SUB_TOTAL[slot] = '';
        });
        const ratioRows = new Set(['MAT_LOSS', 'SUB_LOSS', 'ETC_LOSS', 'MARGIN']);
        hinbanGenkaRowKeyOrder.forEach((rowKey) => {
            if (ratioRows.has(rowKey)) {
                rows[rowKey].syokei = '';
                rows[rowKey].syokei_f = '';
                return;
            }
            const left = n(rowKey, main) + n(rowKey, 'kojo');
            rows[rowKey].syokei = (has(rowKey, main) || has(rowKey, 'kojo') || left !== 0) ? String(roundHinbanGenka(left)) : '';
            if ([5, 8, 9].includes(patternNo)) {
                rows[rowKey].syokei_f = '';
            } else {
                const right = n(rowKey, mainF) + n(rowKey, 'kojo_f');
                rows[rowKey].syokei_f = (has(rowKey, mainF) || has(rowKey, 'kojo_f') || right !== 0) ? String(roundHinbanGenka(right)) : '';
            }
        });
        const expense = n('EXPENSE_OUT', expenseSlot) + n('EXPENSE_IN', expenseSlot) + n('TARIFF', expenseSlot) - n('ZAN8', expenseSlot) + n('POST_SUB', expenseSlot) + n('DOMESTIC_WORK', expenseSlot);
        const mainEst = patternNo === 1
            ? (n('MAT_TOTAL', main) + n('SUB_TOTAL', main) + n('SECONDARY', main) + n('ADJUST', main))
            : (n('MAT_TOTAL', main) + n('SUB_TOTAL', main) + n('SECONDARY', main) + n('ADJUST', main) + expense);
        rows.EST_COST[main] = String(roundHinbanGenka(mainEst));
        let kojoEst = 0;
        if (patternNo === 1) {
            kojoEst = (n('MAT_TOTAL', 'kojo') + n('SUB_TOTAL', 'kojo') + n('PROCESS', 'kojo') + n('SECONDARY', 'kojo')) * (1 + calcHinbanGenkaRate(n('MARGIN', 'kojo'))) + n('ADJUST', 'kojo');
        } else if (patternNo === 2) {
            kojoEst = (n('SUB_TOTAL', 'kojo') + n('PROCESS', 'kojo') + n('SECONDARY', 'kojo')) * (1 + calcHinbanGenkaRate(n('MARGIN', 'kojo'))) + n('ADJUST', 'kojo');
        } else if (patternNo === 3) {
            kojoEst = n('PRODUCT', 'kojo') * (1 + calcHinbanGenkaRate(n('MARGIN', 'kojo'))) + n('ADJUST', 'kojo');
        } else {
            kojoEst = n('PROCESS', 'kojo') * (1 + calcHinbanGenkaRate(n('MARGIN', 'kojo'))) + n('ADJUST', 'kojo');
        }
        rows.EST_COST.kojo = String(roundHinbanGenka(kojoEst));
        if ([5, 7, 9].includes(patternNo)) {
            const kojoFEst = n('PROCESS', 'kojo_f') * (1 + calcHinbanGenkaRate(n('MARGIN', 'kojo_f'))) + n('ADJUST', 'kojo_f');
            rows.EST_COST.kojo_f = String(roundHinbanGenka(kojoFEst));
            if (patternNo === 7) {
                const extMain = n('MAT_TOTAL', 'yusho_f') + n('SUB_TOTAL', 'yusho_f') + n('SECONDARY', 'yusho_f');
                rows.EST_COST.yusho_f = String(roundHinbanGenka(extMain));
                rows.EST_COST.syokei_f = String(roundHinbanGenka(extMain + kojoFEst));
            } else {
                rows.EST_COST.yusho_f = '';
                rows.EST_COST.syokei_f = '';
            }
        } else {
            rows.EST_COST.kojo_f = '';
            rows.EST_COST.yusho_f = '';
            rows.EST_COST.syokei_f = '';
        }
        rows.EST_COST.syokei = String(roundHinbanGenka(n('EST_COST', main) + n('EST_COST', 'kojo')));
        const estSyokei = hinbanGenkaToNumber(rows.EST_COST.syokei);
        hinbanGenkaRowKeyOrder.forEach((rowKey) => {
            if (ratioRows.has(rowKey)) {
                rows[rowKey].kousei = '';
                return;
            }
            const syokeiValue = hinbanGenkaToNumber(DicVal(rows[rowKey], 'syokei', '0'));
            rows[rowKey].kousei = estSyokei === 0 || syokeiValue === 0 ? '' : String(Math.round((syokeiValue / estSyokei) * 100));
        });
        const recalcedDisplayRows = hinbanGenkaRowKeyOrder.map((rowKey, index) => ({
            ...rows[rowKey],
            rowid: String(DicVal(rows[rowKey], 'rowid', index)),
            row_key: rowKey,
            item_name: String(hinbanGenkaRowLabelByKey[rowKey] ?? DicVal(rows[rowKey], 'item_name', ''))
        }));
        const nextRawRows = buildHinbanGenkaSaveRows(recalcedDisplayRows, sourceRows);
        const nextRawRow = { ...(rawHinbanGenkaRow(nextRawRows) || {}) };
        const jyodai = hinbanGenkaToNumber(DicVal(nextRawRow, 'SSDGM_JYODAI', DicVal(entrydataRef.current, 'PDT_PRICE', '0')));
        const shiire = hinbanGenkaToNumber(rows.EST_COST.syokei);
        const settei = shiire === 0 ? 0 : (jyodai / shiire);
        let hachu = 0;
        if (patternNo === 1) hachu = hinbanGenkaToNumber(rows.EST_COST.kojo);
        else if (patternNo === 2) hachu = shiire;
        else if (patternNo === 4) hachu = hinbanGenkaToNumber(rows.PROCESS.syokei);
        else if ([3, 5, 8, 9].includes(patternNo)) hachu = hinbanGenkaToNumber(rows.EST_COST.kojo);
        else if ([6, 7].includes(patternNo)) hachu = (hinbanGenkaToNumber(rows.MAT_TOTAL.syokei) + hinbanGenkaToNumber(rows.SUB_TOTAL.syokei) + hinbanGenkaToNumber(rows.PROCESS.syokei) + hinbanGenkaToNumber(rows.SECONDARY.syokei)) * (1 + calcHinbanGenkaRate(hinbanGenkaToNumber(rows.MARGIN.kojo)));
        let hachuF = '';
        if ([5, 9].includes(patternNo)) hachuF = String(roundHinbanGenka(hinbanGenkaToNumber(rows.EST_COST.kojo_f)));
        if (patternNo === 7) hachuF = String(roundHinbanGenka((hinbanGenkaToNumber(rows.MAT_TOTAL.syokei_f) + hinbanGenkaToNumber(rows.SUB_TOTAL.syokei_f) + hinbanGenkaToNumber(rows.PROCESS.syokei_f) + hinbanGenkaToNumber(rows.SECONDARY.syokei_f)) * (1 + calcHinbanGenkaRate(hinbanGenkaToNumber(rows.MARGIN.kojo_f)))));
        nextRawRow.SSDGM_TSUKATANI = shouldForceGenkaCurrencyJpy(entrydataRef.current) ? 'JPY' : String(genkaCurrency || DicVal(nextRawRow, 'SSDGM_TSUKATANI', ''));
        nextRawRow.SSDGM_RATE = shouldForceGenkaCurrencyJpy(entrydataRef.current) ? '1' : String(DicVal(nextRawRow, 'SSDGM_RATE', ''));
        nextRawRow.SSDGM_SYOKAKARITAKA = String(roundHinbanGenka(expense));
        nextRawRow.SSDGM_SEIHINGENKA = rows.EST_COST.syokei;
        nextRawRow.SSDGM_JYODAIRITU = shiire === 0 ? '' : String(roundHinbanGenka(settei));
        nextRawRow.SSDGM_HACHUTANKA = String(roundHinbanGenka(hachu));
        nextRawRow.SSDGM_HACHUTANKAF = hachuF;
        nextRawRows[0] = nextRawRow;
        applyHinbanGenkaDisplayRows(panelInfo, nextRawRows, recalcedDisplayRows);
        fnc.current.modify(true);
        rerender();
    }

    return {
        hinbanGenkaToNumber,
        roundHinbanGenka,
        calcHinbanGenkaRate,
        hinbanGenkaMainSlotByPatternNo,
        hinbanGenkaMainFSlotByPatternNo,
        hinbanGenkaExpenseSlotByPatternNo,
        hinbanGenkaPatternNoForCalc,
        buildHinbanGenkaCalcRowMap,
        applyHinbanGenkaDisplayRows,
        recalcHinbanGenkaGridAndSummary
    };
}
